import { useEffect, useMemo, useRef } from 'react';

import { useCart } from '$src/hooks/queries';
import { useCartIdAtom } from '$src/hooks/state';

/**
 * Hook to monitor discount code changes in the cart
 * Returns arrays of added and removed discount codes
 * Handles cart ID changes by resetting tracking when cart changes
 */
export const useDiscountCodesWatch = () => {
  const { data: cart } = useCart();
  const { cartId } = useCartIdAtom();
  const lastProcessedDiscountCodes = useRef<string[]>([]);
  const lastProcessedCartId = useRef<string | undefined>(undefined);

  const currentDiscountCodes = useMemo<string[]>(() => {
    return (cart?.discounts?.map((d) => d.code).filter(Boolean) as string[]) || [];
  }, [cart]);

  // Reset tracking when cart ID changes
  useEffect(() => {
    if (cartId !== lastProcessedCartId.current) {
      lastProcessedDiscountCodes.current = [];
      lastProcessedCartId.current = cartId;
    }
  }, [cartId]);

  const addedDiscountCodes = currentDiscountCodes.filter(
    (code) => !lastProcessedDiscountCodes.current.includes(code),
  );
  const removedDiscountCodes = lastProcessedDiscountCodes.current.filter(
    (code) => !currentDiscountCodes.includes(code),
  );

  useEffect(() => {
    lastProcessedDiscountCodes.current = [...currentDiscountCodes];
  }, [currentDiscountCodes]);

  return {
    addedDiscountCodes,
    removedDiscountCodes,
    currentDiscountCodes,
  };
};
